<template>
  <AuthenticatedLayout activeMenuItem='report-annual-portfolio' title='Reports'>
    <LoadingLayout :isLoading='!pageReady'>
      <PaddedLayout>
        <div class='flex flex-wrap flex-col sm:flex-row py-2 justify-between sm:items-center gap-2 w-full max-w-[80rem]'>
          <h1 class='text-2xl'>Annual Portfolio Report</h1>
          <a :href='pdfUrl' target='_blank' type="submit" class="rounded-md bg-jananavy-900 px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-jananavy-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-jananavy-600">Export PDF</a>
        </div>
        <div class='flex gap-2 items-center mt-2 max-w-[80rem]'>
          <SingleRelationInput collection='assessment-template' :srLabel='true' label='Assessment Template' v-model='state.filters.assessmentTemplate' placeholder='Search Template...' class='w-full' />
          <TabbedNumberInput v-model='state.filters.year.from' label='Compare To' :hideLabel='true' class='w-full' />
          <TabbedNumberInput v-model='state.filters.year.to' label='Year' :hideLabel='true' class='w-full' />
        </div>
        <div class='mt-2'>
          <TabView :tabs='tabs' v-model='state.tab' />

          <div v-if='state.tab === "results"' class='mt-8'>
            <GraphAnnualPortfolioTable
              :params='params'
              :showOrganisation='isSuperAdminView()'
              :showAssetManager='true'
              :view='view'
              :showFilter='true'
            >
              <template #title>Results</template>
              <template #description>Scores for all engagements.</template>
            </GraphAnnualPortfolioTable>
          </div>

          <div v-if='state.tab === "themes"' class='mt-8 max-w-[80rem]'>
            <GraphAnnualThemePie :params='params' :showKey='true'>
              <template #title>Theme Performance</template>
              <template #description>The distribution of engagement scores by theme for your organisation.</template>
            </GraphAnnualThemePie>
          </div>

          <div v-if='state.tab === "assets"' class='mt-8 max-w-[80rem]'>
            <GraphAnnualEntityBar :params='params' :dimension='`${view}Asset`' :showKey='true'>
              <template #title>Asset Performance</template>
              <template #description>The distribution of engagement scores by asset for your organisation.</template>
            </GraphAnnualEntityBar>
          </div>

          <div v-if='state.tab === "assetManagers"' class='mt-8 max-w-[80rem]'>
            <GraphAnnualEntityBar :params='params' :dimension='`${view}AssetManager`' :showKey='true'>
              <template #title>Asset Managers Performance</template>
              <template #description>The distribution of engagement scores by asset manager for your organisation.</template>
            </GraphAnnualEntityBar>
          </div>

          <div v-if='state.tab === "change"' class='mt-8 max-w-[80rem]'>
            <GraphAnnualChangeBar :params='params'>
              <template #title>Change</template>
              <template #description>Percentages of engagements that had a positive shift.</template>
            </GraphAnnualChangeBar>
          </div>

        </div>
      </PaddedLayout>
    </LoadingLayout>
  </AuthenticatedLayout>
</template>

<script setup>

import { computed, reactive, watch, ref, onActivated } from 'vue';

import { usePayloadRequest } from '@/kernel/api';
import { debounce } from '@/kernel/debounce';
import { appendStateToRoute, getStateFromRoute } from '@/router';
import { dehydrateCollections, rehydrateCollections, getReportPDFExportUrl } from '@/kernel/report';

import AuthenticatedLayout from '@/components/layouts/authenticated-layout.vue';
import PaddedLayout from '@/components/layouts/padded-layout.vue';
import LoadingLayout from '@/components/layouts/loading-layout.vue';

import TabView from '@/components/views/tab-view.vue';

import ErrorNotice from '@/components/notices/error-notice.vue';

import SingleRelationInput from '@/components/inputs/single-relation-input.vue';
import TabbedNumberInput from '@/components/inputs/tabbed-number-input.vue';

import GraphAnnualPortfolioTable from '@/components/reports/graphs/graph-annual-portfolio-table.vue';
import GraphAnnualThemePie from '@/components/reports/graphs/graph-annual-theme-pie.vue';
import GraphAnnualEntityBar from '@/components/reports/graphs/graph-annual-entity-bar.vue';
import GraphAnnualChangeBar from '@/components/reports/graphs/graph-annual-change-bar.vue';

import { getOrganisationId, isSuperAdminView, isOrganisationView } from '@/kernel/session';

const tabs = [
  { name: 'Results', key: 'results' },
  { name: 'Themes', key: 'themes' },
  { name: 'Assets', key: 'assets' },
  { name: 'Asset Managers', key: 'assetManagers' },
  { name: 'Change', key: 'change' }
];

const organisationId = computed(() => {
  return getOrganisationId();
});

const view = computed(() => {
  return isSuperAdminView() ? 'platform' : 'organisation';
});

const filterCollectionMap = [
  {key: 'assessmentTemplate', collection: 'assessment-template'}
];

const getDefaultState = (preload) => {
  return {
    tab: preload?.tab || 'results',
    filters: {
      year: {
        from: preload?.filters?.year?.from || new Date().getFullYear() - 1,
        to: preload?.filters?.year?.to || new Date().getFullYear()
      },
      assessmentTemplate: preload?.filters?.assessmentTemplate || null
    }
  }
}

const state = reactive(getDefaultState());
const pageReady = ref(false);
const routeState = getStateFromRoute();
const pdfUrl = ref(getReportPDFExportUrl());
const params = ref(null);

const stateDidChange = (doUpdateParams = false) => {
  const collectionFilters = dehydrateCollections(filterCollectionMap, state.filters);

  appendStateToRoute({
    ...state,
    filters: {
      ...state.filters,
      ...collectionFilters
    }
  });

  setTimeout(() => {
    pdfUrl.value = getReportPDFExportUrl();
  }, 100);

  if (doUpdateParams) {
    updateParams();
  }
}

const updateParams = debounce(() => {
  params.value = calcParams();
}, 250);

const calcParams = () => {
  if (state.filters.year.from.toString().length !== 4) {
    return null;
  }

  if (state.filters.year.to.toString().length !== 4) {
    return null;
  }

  if (state.filters.year.from === state.filters.year.to) {
    return null;
  }

  const args = {
    resolution: 'year',
    year: state.filters.year
  }

  if (state.filters.assessmentTemplate) {
    args.assessmentTemplate = state.filters.assessmentTemplate.id;
  }

  if (isOrganisationView()) {
    args.organisation = [getOrganisationId()];
  }

  return args;
}

const loadState = async () => {
  if (routeState?.filters) {
    const defaultState = getDefaultState(routeState);

    const collectionFilters = await rehydrateCollections(filterCollectionMap, defaultState.filters);

    defaultState.filters = {
      ...defaultState.filters,
      ...collectionFilters
    }

    Object.assign(state, defaultState);
  }

  pageReady.value = true;

  watch(organisationId, () => {
    stateDidChange(true);
  });

  watch(() => state.tab, () => {
    stateDidChange(false);
  });

  watch(() => state.filters.year.from, () => {
    stateDidChange(true);
  });

  watch(() => state.filters.year.to, () => {
    stateDidChange(true);
  });

  watch(() => state.filters.assessmentTemplate, () => {
    stateDidChange(true);
  });

  stateDidChange(true);
}

onActivated(() => {
  if (pageReady.value) {
    stateDidChange();
  } else {
    loadState();
  }
});

</script>
